// Keeps long-lived sessions fresh. The installed Kipara app (PWA) keeps one
// page alive for days: after a deploy it goes on running the old build, and
// data fetched at page-mount goes stale while the app sits in the background.
// The browser "site" only feels fresher because every visit reloads the page.
//
// Two mechanisms close that gap:
//  1. Build check — compare the running main bundle against the one in
//     /asset-manifest.json (every 60 s while visible + when the app returns
//     to the foreground). On mismatch, offer a one-tap « Mettre à jour »
//     which purges the SW caches itself — no manual cache clearing.
//  2. Data refresh — when the app comes back after 15+ min in the background
//     the current page is remounted so lists/stock/stats are re-fetched. Skipped on /add and
//     /edit screens, where a reload would wipe unsaved input (the sale cart
//     lives only in memory).
import Swal from './theme/swal';
import KI from './theme/palette';
import { invalidateApiCache } from './services/apiCache';

const VERSION_POLL_MS = 60 * 1000;
const STALE_AFTER_MS = 15 * 60 * 1000;

// Screens holding unsaved input — never auto-reload these.
const hasUnsavedInputRoute = () => /\/(add|edit|new)(\/|$)/.test(window.location.pathname);

function runningMainBundle() {
  // CRA emits a content-hashed /static/js/main.<hash>.js; absent on the dev server.
  const el = document.querySelector('script[src*="/static/js/main."]');
  return el ? new URL(el.getAttribute('src'), window.location.href).pathname : null;
}

async function deployedMainBundle() {
  const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
  if (!res.ok) return null;
  const manifest = await res.json();
  return (manifest.files && manifest.files['main.js']) || null;
}

let promptOpen = false;

async function checkForNewBuild() {
  const running = runningMainBundle();
  if (!running || promptOpen) return;
  let deployed;
  try {
    deployed = await deployedMainBundle();
  } catch {
    return; // offline / transient network error — try again next cycle
  }
  if (!deployed || deployed === running) return;
  promptOpen = true;
  const { isConfirmed } = await Swal.fire({
    icon: 'info',
    title: 'Mise à jour disponible',
    text: "Une nouvelle version de l'application est prête.",
    confirmButtonText: 'Mettre à jour',
    // Explicit color (inline style): Tailwind's base reset strips the swal2
    // stylesheet default, leaving the button white-on-white until hovered.
    confirmButtonColor: KI.ramps.blue[600],
  });
  promptOpen = false;
  if (isConfirmed) {
    // Le bouton fait le ménage lui-même : purge des caches du service worker
    // (shell/assets/api/media) + bascule sur le SW en attente, puis reload.
    // L'utilisateur n'a jamais à « vider le cache » à la main.
    try {
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          if (reg.waiting) reg.waiting.postMessage('SKIP_WAITING');
          await reg.update().catch(() => {});
        }
      }
    } catch {
      // Une purge ratée ne doit pas bloquer la mise à jour.
    }
    window.location.reload();
  }
}

export default function startAutoRefresh() {
  let hiddenAt = null;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    const hiddenFor = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = null;
    if (hiddenFor >= STALE_AFTER_MS && !hasUnsavedInputRoute()) {
      // Soft refresh: drop cached answers and remount the current page only
      // (components/RefreshableOutlet) — no full reload, no blank app.
      invalidateApiCache();
      window.dispatchEvent(new Event('kipara:soft-refresh'));
    }
    checkForNewBuild();
  });
  setInterval(() => {
    if (document.visibilityState === 'visible') checkForNewBuild();
  }, VERSION_POLL_MS);
}
